import { makeFilterDocumentSchema, makeStringConditionSchema, matchesStringOp, } from "./filterDocumentPrimitives.js";
// ---------------------------------------------------------------------------
// Scope — the one derived family
// ---------------------------------------------------------------------------
/**
 * The system accounts a user works with directly and so sees in a fresh grid
 * — the SAME three the legacy list endpoint's `.or()` kept
 * (`apps/api/src/routes/accounting/chart-of-accounts.ts`). Every other
 * `system_account_type` (fx_clearing, the VAT family, opening_balance_equity,
 * retained_earnings, channel_clearing, …) is internal plumbing and reads as
 * 'system'. Exported so the shared test pins the list.
 */
export const USER_FACING_SYSTEM_ACCOUNT_TYPES = [
    "suspense",
    "accounts_receivable",
    "accounts_payable",
];
/**
 * 'system' iff the account is a system account whose type is NOT one of the
 * three user-facing ones; everything else — every user account, plus
 * suspense / AR / AP — is 'standard'. `is_system_account` is checked first so
 * an (impossible today) NULL `system_account_type` on a system row still
 * reads as 'system' rather than falling through the list check.
 */
export function deriveChartOfAccountScope(row) {
    if (!row.is_system_account)
        return "standard";
    const userFacing = USER_FACING_SYSTEM_ACCOUNT_TYPES;
    return row.system_account_type != null && userFacing.includes(row.system_account_type)
        ? "standard"
        : "system";
}
// ---------------------------------------------------------------------------
// Field vocabulary (v1)
// ---------------------------------------------------------------------------
/**
 * The five families. Three are CLOSED vocabularies — `account.scope`
 * ('standard' | 'system'), `account.status` ('active' | 'inactive') and
 * `account.normal_balance` ('debit' | 'credit') — but stay plain string
 * families here: `values` is the panel's selection and the row accessors are
 * typed `string`, so a narrower mapper union satisfies them structurally (the
 * `timesheet.visibility` precedent). The two ID families carry uuids.
 */
export const CHART_OF_ACCOUNT_FILTER_STRING_FIELDS = [
    "account.scope",
    "account.status",
    "account.category_id",
    "account.type_id",
    "account.normal_balance",
];
// ---------------------------------------------------------------------------
// Conditions
// ---------------------------------------------------------------------------
/**
 * ONE family, so no union — every condition is a string condition. The
 * evaluator still narrows on `op` (the sibling invariant), so adding a second
 * family later is a new union arm, not a rewrite.
 *
 * EMPTY-SET SEMANTICS (from the shared primitives, not re-decided here): an
 * `in` condition with zero values is REJECTED by the schema. "Nothing
 * checked" is spelled as NO CONDITION AT ALL — the panel drops the family —
 * and never as a condition that would match nothing.
 */
export const chartOfAccountFilterConditionSchema = makeStringConditionSchema(CHART_OF_ACCOUNT_FILTER_STRING_FIELDS);
// ---------------------------------------------------------------------------
// The document
// ---------------------------------------------------------------------------
export const chartOfAccountFilterDocumentSchema = makeFilterDocumentSchema(chartOfAccountFilterConditionSchema);
/** The empty document — "no filters active" (what Clear resets to). */
export const EMPTY_CHART_OF_ACCOUNT_FILTER_DOCUMENT = {
    version: 1,
    match: "all",
    conditions: [],
};
/**
 * What a FRESH grid starts on: standard accounts only (user accounts plus
 * suspense / AR / AP). See the docblock for the three accepted consequences
 * (badge reads 1 on load; Clear goes to EMPTY, not back to here;
 * `hasUpstreamFilters` is removal-based).
 */
export const DEFAULT_CHART_OF_ACCOUNT_FILTER_DOCUMENT = {
    version: 1,
    match: "all",
    conditions: [{ field: "account.scope", op: "in", values: ["standard"] }],
};
/*
 * One accessor for the one family: an exhaustive switch over the vocabulary
 * with NO default case (the reason the siblings keep them: a new vocabulary
 * entry is a `TS2366: Function lacks ending return statement` until it is
 * handled here, instead of compiling clean and filtering the wrong column).
 * No `?? ''` fallbacks — nothing here is nullable.
 */
function stringValue(row, field) {
    switch (field) {
        case "account.scope":
            return row.scope;
        case "account.status":
            return row.status;
        case "account.category_id":
            return row.category_id;
        case "account.type_id":
            return row.type_id;
        case "account.normal_balance":
            return row.normal_balance;
    }
}
// One family today; still narrowed on `op` so a second arm slots in later.
function matchesCondition(row, c) {
    switch (c.op) {
        case "in":
        case "is_empty":
            return matchesStringOp(c.op, stringValue(row, c.field), c.values, c.include_empty);
    }
}
/**
 * Apply a filter document to mapped account rows (AND of all conditions — v1
 * `match` is 'all' only). An empty document returns the input array unchanged
 * (identity — callers can rely on reference equality to skip re-renders).
 */
export function applyChartOfAccountFilterDocument(rows, doc) {
    if (doc.conditions.length === 0)
        return rows;
    return rows.filter((row) => doc.conditions.every((c) => matchesCondition(row, c)));
}
/** Count of active conditions — the filter button's badge (1 on a fresh grid). */
export function countActiveChartOfAccountConditions(doc) {
    return doc.conditions.length;
}
//# sourceMappingURL=chartOfAccountFilters.js.map